import React, { useEffect, useState } from "react";

import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import ReactDOMServer from "react-dom/server";

import {
  Body,
  Button,
  Container,
  Head,
  Heading,
  Hr,
  Html,
  Img,
  Preview,
  Section,
  Text,
} from "@react-email/components";
import { render } from "@react-email/render";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  ImageIcon,
  Save,
  Settings,
  Type,
} from "lucide-react";

import { Button as UIButton } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

// Types for drag-and-drop
type ItemTypes = {
  TEXT: "text";
  HEADING: "heading";
  BUTTON: "button";
  IMAGE: "image";
  DIVIDER: "divider";
  SPACER: "spacer";
};

const ItemType: ItemTypes = {
  TEXT: "text",
  HEADING: "heading",
  BUTTON: "button",
  IMAGE: "image",
  DIVIDER: "divider",
  SPACER: "spacer",
};

interface DragItem {
  type: string;
  id: string;
  index: number;
}

interface EmailElement {
  id: string;
  type: string;
  content: string;
  href?: string;
  src?: string;
  alt?: string;
  color?: string;
  backgroundColor?: string;
  size?: "small" | "medium" | "large";
  align?: "left" | "center" | "right";
  headingType?: "h1" | "h2" | "h3";
  spacerHeight?: number;
  fontFamily?: string;
  fontSize?: string;
  textColor?: string;
  imgWidth?: string;
  imgHeight?: string;
  imgAlignment?: "left" | "center" | "right";
}

// Add type definitions for props
interface DraggableElementProps {
  element: EmailElement;
  index: number;
  moveElement: (dragIndex: number, hoverIndex: number) => void;
  onSelectElement: (id: string) => void;
  onDoubleClickElement: (id: string) => void;
  isSelected: boolean;
  onDeleteElement: (id: string) => void;
}

interface ElementConfigPanelProps {
  selectedElement: EmailElement;
  updateElement: (id: string, updatedElement: EmailElement) => void;
  contentEditRef: React.RefObject<HTMLTextAreaElement>;
}

// Draggable email components
const DraggableElement: React.FC<DraggableElementProps> = ({
  element,
  index,
  moveElement,
  onSelectElement,
  onDoubleClickElement,
  isSelected,
  onDeleteElement,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: Object.values(ItemType),
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      moveElement(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: element.type,
    item: () => {
      return { id: element.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const renderElementPreview = () => {
    switch (element.type) {
      case ItemType.TEXT:
        return (
          <div
            className="px-2 py-2"
            style={{
              color: element.textColor || "inherit",
              fontFamily: element.fontFamily || "inherit",
              fontSize: element.fontSize || "inherit",
              textAlign: element.align || "left",
            }}
          >
            {element.content}
          </div>
        );
      case ItemType.HEADING:
        return (
          <div
            className={cn(
              "px-2 py-2 font-bold",
              element.headingType === "h1"
                ? "text-xl"
                : element.headingType === "h2"
                  ? "text-lg"
                  : "text-base"
            )}
            style={{
              color: element.textColor || "inherit",
              fontFamily: element.fontFamily || "inherit",
              fontSize: element.fontSize || "inherit",
              textAlign: element.align || "left",
            }}
          >
            {element.content}
          </div>
        );
      case ItemType.BUTTON:
        return (
          <div
            className="px-2 py-2"
            style={{ textAlign: element.align || "left" }}
          >
            <button
              className="rounded px-4 py-2 text-white"
              style={{
                backgroundColor: element.backgroundColor || "#000",
                color: element.textColor || "white",
                fontFamily: element.fontFamily || "inherit",
                fontSize: element.fontSize || "inherit",
              }}
            >
              {element.content}
            </button>
          </div>
        );
      case ItemType.IMAGE:
        return (
          <div
            className="px-2 py-2"
            style={{
              textAlign: element.imgAlignment || element.align || "left",
            }}
          >
            {element.src ? (
              <img
                src={element.src}
                alt={element.alt || "Email image"}
                className="h-auto max-w-full"
                style={{
                  width: element.imgWidth || "auto",
                  height: element.imgHeight || "auto",
                }}
              />
            ) : (
              <div className="flex h-32 items-center justify-center border border-gray-300 bg-gray-100">
                <ImageIcon className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
        );
      case ItemType.DIVIDER:
        return <hr className="my-4 border-t border-gray-300" />;
      case ItemType.SPACER:
        return (
          <div style={{ height: `${element.spacerHeight || 20}px` }}></div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      style={{ opacity }}
      className={cn(
        "mb-2 cursor-move rounded-md border",
        isSelected ? "border-primary ring-1 ring-primary" : "border-gray-200",
        isDragging ? "opacity-40" : "opacity-100"
      )}
      onClick={() => onSelectElement(element.id)}
      onDoubleClick={() => onDoubleClickElement(element.id)}
    >
      <div className="flex items-center justify-between border-b bg-gray-50 p-2">
        <div className="text-xs font-medium text-gray-500">
          {element.type.charAt(0).toUpperCase() + element.type.slice(1)}
        </div>
        <div className="flex gap-1">
          <button
            className="rounded p-1 hover:bg-gray-200"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteElement(element.id);
            }}
          >
            <AlertCircle className="h-3 w-3 text-red-500" />
          </button>
        </div>
      </div>
      {renderElementPreview()}
    </div>
  );
};

// Element configuration panel
const ElementConfigPanel: React.FC<ElementConfigPanelProps> = ({
  selectedElement,
  updateElement,
  contentEditRef,
}) => {
  if (!selectedElement) return null;

  const handleChange = (field: string, value: unknown) => {
    updateElement(selectedElement.id, { ...selectedElement, [field]: value });
  };

  const renderCommonOptions = () => (
    <>
      {(selectedElement.type === ItemType.TEXT ||
        selectedElement.type === ItemType.HEADING ||
        selectedElement.type === ItemType.BUTTON) && (
        <>
          <div className="mb-4">
            <Label htmlFor="content">Content</Label>
            <Textarea
              id="content"
              ref={contentEditRef}
              value={selectedElement.content}
              onChange={(e) => handleChange("content", e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="textColor">Text Color</Label>
            <div className="mt-1 flex">
              <Input
                id="textColor"
                type="color"
                value={selectedElement.textColor || "#000000"}
                onChange={(e) => handleChange("textColor", e.target.value)}
                className="h-10 w-12 p-1"
              />
              <Input
                type="text"
                value={selectedElement.textColor || "#000000"}
                onChange={(e) => handleChange("textColor", e.target.value)}
                className="ml-2 flex-1"
                placeholder="#000000"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label htmlFor="fontFamily">Font Family</Label>
            <select
              id="fontFamily"
              value={selectedElement.fontFamily || "Arial, sans-serif"}
              onChange={(e) => handleChange("fontFamily", e.target.value)}
              className="mt-1 w-full rounded border p-2"
            >
              <option value="Arial, sans-serif">Arial</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="Verdana, sans-serif">Verdana</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Roboto', sans-serif">Roboto</option>
            </select>
          </div>

          <div className="mb-4">
            <Label htmlFor="fontSize">Font Size</Label>
            <select
              id="fontSize"
              value={selectedElement.fontSize || "inherit"}
              onChange={(e) => handleChange("fontSize", e.target.value)}
              className="mt-1 w-full rounded border p-2"
            >
              <option value="12px">Small (12px)</option>
              <option value="14px">Medium (14px)</option>
              <option value="16px">Default (16px)</option>
              <option value="18px">Large (18px)</option>
              <option value="24px">X-Large (24px)</option>
              <option value="32px">XX-Large (32px)</option>
            </select>
          </div>
        </>
      )}

      {(selectedElement.type === ItemType.TEXT ||
        selectedElement.type === ItemType.HEADING) && (
        <div className="mb-4">
          <Label htmlFor="align">Alignment</Label>
          <select
            id="align"
            value={selectedElement.align || "left"}
            onChange={(e) => handleChange("align", e.target.value)}
            className="mt-1 w-full rounded border p-2"
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>
      )}
    </>
  );

  const renderSpecificOptions = () => {
    switch (selectedElement.type) {
      case ItemType.HEADING:
        return (
          <div className="mb-4">
            <Label htmlFor="headingType">Heading Size</Label>
            <select
              id="headingType"
              value={selectedElement.headingType || "h2"}
              onChange={(e) => handleChange("headingType", e.target.value)}
              className="mt-1 w-full rounded border p-2"
            >
              <option value="h1">Large (H1)</option>
              <option value="h2">Medium (H2)</option>
              <option value="h3">Small (H3)</option>
            </select>
          </div>
        );
      case ItemType.BUTTON:
        return (
          <>
            <div className="mb-4">
              <Label htmlFor="href">Button Link</Label>
              <Input
                id="href"
                type="url"
                value={selectedElement.href || ""}
                onChange={(e) => handleChange("href", e.target.value)}
                placeholder="https://example.com"
                className="mt-1"
              />
            </div>
            <div className="mb-4">
              <Label htmlFor="backgroundColor">Button Color</Label>
              <div className="mt-1 flex">
                <Input
                  id="backgroundColor"
                  type="color"
                  value={selectedElement.backgroundColor || "#000000"}
                  onChange={(e) =>
                    handleChange("backgroundColor", e.target.value)
                  }
                  className="h-10 w-12 p-1"
                />
                <Input
                  type="text"
                  value={selectedElement.backgroundColor || "#000000"}
                  onChange={(e) =>
                    handleChange("backgroundColor", e.target.value)
                  }
                  className="ml-2 flex-1"
                />
              </div>
            </div>
            <div className="mb-4">
              <Label htmlFor="align">Button Alignment</Label>
              <select
                id="align"
                value={selectedElement.align || "center"}
                onChange={(e) => handleChange("align", e.target.value)}
                className="mt-1 w-full rounded border p-2"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </>
        );
      case ItemType.IMAGE:
        return (
          <>
            <div className="mb-4">
              <Label htmlFor="src">Image URL</Label>
              <Input
                id="src"
                type="url"
                value={selectedElement.src || ""}
                onChange={(e) => handleChange("src", e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="mt-1"
              />
            </div>
            <div className="mb-4">
              <Label htmlFor="alt">Alt Text</Label>
              <Input
                id="alt"
                type="text"
                value={selectedElement.alt || ""}
                onChange={(e) => handleChange("alt", e.target.value)}
                placeholder="Image description"
                className="mt-1"
              />
            </div>

            <div className="mb-4">
              <Label htmlFor="imgAlignment">Image Alignment</Label>
              <select
                id="imgAlignment"
                value={selectedElement.imgAlignment || "center"}
                onChange={(e) => handleChange("imgAlignment", e.target.value)}
                className="mt-1 w-full rounded border p-2"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>

            <div className="mb-4">
              <Label htmlFor="imgWidth">Width</Label>
              <div className="mt-1 flex items-center">
                <select
                  id="imgWidth"
                  value={selectedElement.imgWidth || "auto"}
                  onChange={(e) => handleChange("imgWidth", e.target.value)}
                  className="w-full rounded border p-2"
                >
                  <option value="auto">Auto</option>
                  <option value="100%">100% (Full width)</option>
                  <option value="75%">75%</option>
                  <option value="50%">50%</option>
                  <option value="25%">25%</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <Label htmlFor="imgHeight">Height</Label>
              <div className="mt-1 flex items-center">
                <select
                  id="imgHeight"
                  value={selectedElement.imgHeight || "auto"}
                  onChange={(e) => handleChange("imgHeight", e.target.value)}
                  className="w-full rounded border p-2"
                >
                  <option value="auto">Auto</option>
                  <option value="150px">Small (150px)</option>
                  <option value="300px">Medium (300px)</option>
                  <option value="450px">Large (450px)</option>
                </select>
              </div>
            </div>
          </>
        );
      case ItemType.SPACER:
        return (
          <div className="mb-4">
            <Label htmlFor="spacerHeight">Height (px)</Label>
            <Input
              id="spacerHeight"
              type="number"
              min="5"
              max="100"
              value={selectedElement.spacerHeight || 20}
              onChange={(e) =>
                handleChange("spacerHeight", parseInt(e.target.value))
              }
              className="mt-1"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-md border bg-gray-50 p-4">
      <h3 className="mb-4 font-medium">Edit {selectedElement.type}</h3>
      {renderCommonOptions()}
      {renderSpecificOptions()}
    </div>
  );
};

// Email template builder component
export const EmailTemplateBuilder: React.FC<{
  initialHtml?: string;
  onSave?: (html: string) => void;
}> = ({ initialHtml, onSave }) => {
  const [elements, setElements] = useState<EmailElement[]>([
    {
      id: "heading-1",
      type: ItemType.HEADING,
      content: "Payment Reminder",
      headingType: "h1",
      align: "center",
    },
    {
      id: "text-1",
      type: ItemType.TEXT,
      content:
        "Dear {{clientName}},\n\nThis is a friendly reminder that your payment of {{amount}} is due on {{dueDate}}.\n\nPlease ensure timely payment to avoid any late fees.",
      align: "left",
    },
    {
      id: "button-1",
      type: ItemType.BUTTON,
      content: "Pay Now",
      href: "#",
      backgroundColor: "#4F46E5",
    },
    {
      id: "text-2",
      type: ItemType.TEXT,
      content:
        "If you have already made this payment, please disregard this reminder.\n\nThank you,\nFinance Team",
      align: "left",
    },
  ]);

  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    null
  );
  const [renderedHtml, setRenderedHtml] = useState<string>("");
  // Changed to true to show preview by default
  const [previewMode, setPreviewMode] = useState(true);
  // We no longer need the activeTab state since we're removing the tabs
  const contentEditRef = React.useRef<HTMLTextAreaElement>(null);

  const addElement = (type: string) => {
    const newElement: EmailElement = {
      id: `${type}-${Date.now()}`,
      type,
      content:
        type === ItemType.HEADING
          ? "New Heading"
          : type === ItemType.BUTTON
            ? "Click Me"
            : type === ItemType.TEXT
              ? "Enter your text here"
              : "",
    };

    if (type === ItemType.HEADING) {
      newElement.headingType = "h2";
      newElement.align = "left";
    }

    if (type === ItemType.BUTTON) {
      newElement.backgroundColor = "#4F46E5";
      newElement.href = "#";
    }

    if (type === ItemType.IMAGE) {
      newElement.src = "";
      newElement.alt = "Image description";
    }

    if (type === ItemType.SPACER) {
      newElement.spacerHeight = 20;
    }

    setElements([...elements, newElement]);
    setSelectedElementId(newElement.id);
    // When adding a new element, automatically switch to edit mode
    setPreviewMode(false);
  };

  const moveElement = (dragIndex: number, hoverIndex: number) => {
    const draggedElement = elements[dragIndex];
    const newElements = [...elements];
    newElements.splice(dragIndex, 1);
    newElements.splice(hoverIndex, 0, draggedElement);
    setElements(newElements);
  };

  const updateElement = (id: string, updatedElement: EmailElement) => {
    setElements(elements.map((el) => (el.id === id ? updatedElement : el)));
  };

  const deleteElement = (id: string) => {
    setElements(elements.filter((el) => el.id !== id));
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  };

  const selectedElement = elements.find((el) => el.id === selectedElementId);

  const generateHtml = async () => {
    const emailJsx = (
      <Html>
        <Head />
        <Preview>Payment Reminder</Preview>
        <Body
          style={{
            fontFamily: "Arial, sans-serif",
            margin: "0",
            padding: "0",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Container
            style={{
              maxWidth: "600px",
              margin: "0 auto",
              backgroundColor: "#ffffff",
              padding: "20px",
            }}
          >
            {elements.map((element) => {
              switch (element.type) {
                case ItemType.TEXT:
                  return (
                    <Text
                      key={element.id}
                      style={{
                        textAlign: element.align || "left",
                        margin: "10px 0",
                        lineHeight: "1.5",
                        whiteSpace: "pre-line",
                        color: element.textColor || "inherit",
                        fontFamily: element.fontFamily || "inherit",
                        fontSize: element.fontSize || "inherit",
                      }}
                    >
                      {element.content}
                    </Text>
                  );
                case ItemType.HEADING:
                  return (
                    <Heading
                      key={element.id}
                      as={element.headingType || "h2"}
                      style={{
                        textAlign: element.align || "left",
                        margin: "15px 0",
                        color: element.textColor || "inherit",
                        fontFamily: element.fontFamily || "inherit",
                        fontSize: element.fontSize || "inherit",
                      }}
                    >
                      {element.content}
                    </Heading>
                  );
                case ItemType.BUTTON:
                  return (
                    <Section
                      key={element.id}
                      style={{
                        textAlign: element.align || "center",
                        margin: "20px 0",
                      }}
                    >
                      <Button
                        href={element.href || "#"}
                        style={{
                          backgroundColor: element.backgroundColor || "#000000",
                          color: element.textColor || "#ffffff",
                          padding: "12px 20px",
                          borderRadius: "4px",
                          textDecoration: "none",
                          fontWeight: "bold",
                          fontFamily: element.fontFamily || "inherit",
                          fontSize: element.fontSize || "inherit",
                        }}
                      >
                        {element.content}
                      </Button>
                    </Section>
                  );
                case ItemType.IMAGE:
                  return (
                    <Section
                      key={element.id}
                      style={{
                        margin: "15px 0",
                        textAlign:
                          element.imgAlignment || element.align || "center",
                      }}
                    >
                      {element.src && (
                        <Img
                          src={element.src}
                          alt={element.alt || ""}
                          width={element.imgWidth || "100%"}
                          height={element.imgHeight || "auto"}
                          style={{
                            width: element.imgWidth || "auto",
                            height: element.imgHeight || "auto",
                            maxWidth: "100%",
                          }}
                        />
                      )}
                    </Section>
                  );
                case ItemType.DIVIDER:
                  return (
                    <Hr
                      key={element.id}
                      style={{
                        margin: "15px 0",
                        borderTop: "1px solid #e5e5e5",
                      }}
                    />
                  );
                case ItemType.SPACER:
                  return (
                    <Section
                      key={element.id}
                      style={{ height: `${element.spacerHeight || 20}px` }}
                    />
                  );
                default:
                  return null;
              }
            })}
          </Container>
        </Body>
      </Html>
    );

    try {
      // First try the async react-email render method
      try {
        const html = await render(emailJsx);
        return html;
      } catch (asyncError) {
        console.error(
          "Async rendering failed, falling back to ReactDOMServer:",
          asyncError
        );
        // Fall back to React's built-in server rendering
        return ReactDOMServer.renderToStaticMarkup(emailJsx);
      }
    } catch (error) {
      console.error("All rendering methods failed:", error);
      // Return a simple fallback HTML template that includes the template variables
      return `<div style="font-family: Arial, sans-serif; padding: 20px; background-color: #f8f9fa; border-radius: 4px;">
                <h1 style="color: #333;">Payment Reminder</h1>
                <p>Dear {{clientName}},</p>
                <p>This is a friendly reminder that your payment of {{amount}} is due on {{dueDate}}.</p>
                <p>Please ensure timely payment to avoid any late fees.</p>
                <div style="margin: 20px 0; text-align: center;">
                  <a href="#" style="background-color: #4F46E5; color: white; padding: 10px 20px; text-decoration: none; border-radius: 4px; font-weight: bold;">Pay Now</a>
                </div>
                <p>If you have already made this payment, please disregard this reminder.</p>
                <p>Thank you,<br>Finance Team</p>
              </div>`;
    }
  };

  // Update rendered HTML whenever elements change or preview mode is toggled
  useEffect(() => {
    if (previewMode) {
      const updateRenderedHtml = async () => {
        const html = await generateHtml();
        setRenderedHtml(html);
      };

      updateRenderedHtml();
    }
  }, [previewMode, elements]);

  const handleSave = async () => {
    const html = await generateHtml();
    if (onSave) {
      onSave(html);
    }
  };

  const copyHtml = async () => {
    const html = await generateHtml();
    navigator.clipboard.writeText(html);
    // You could add a toast notification here
  };

  // Add method to handle double-click on elements
  const handleElementDoubleClick = (elementId: string) => {
    setSelectedElementId(elementId);
    // Switch to edit mode on double click
    setPreviewMode(false);
    // Use setTimeout to ensure the textarea is rendered before attempting to focus
    setTimeout(() => {
      if (contentEditRef.current) {
        contentEditRef.current.focus();
      }
    }, 0);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="mb-4">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Email Template Builder</h2>
          <div className="flex gap-2">
            <UIButton
              size="sm"
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? (
                <>
                  <Settings className="mr-1 h-4 w-4" />
                  Editor Visual
                </>
              ) : (
                <>
                  <Eye className="mr-1 h-4 w-4" />
                  Preview
                </>
              )}
            </UIButton>
            <UIButton size="sm" variant="outline" onClick={copyHtml}>
              <Copy className="mr-1 h-4 w-4" />
              Copy HTML
            </UIButton>
            <UIButton size="sm" onClick={handleSave}>
              <Save className="mr-1 h-4 w-4" />
              Save Template
            </UIButton>
          </div>
        </div>

        {previewMode ? (
          <Card className="border">
            <CardContent className="p-0">
              <div
                className="rounded-md bg-white p-4"
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8">
              <Card className="h-full border">
                <CardContent className="p-4">
                  <div className="min-h-[500px] rounded-md border bg-white p-4">
                    {elements.map((element, index) => (
                      <DraggableElement
                        key={element.id}
                        element={element}
                        index={index}
                        moveElement={moveElement}
                        onSelectElement={setSelectedElementId}
                        onDoubleClickElement={handleElementDoubleClick}
                        isSelected={selectedElementId === element.id}
                        onDeleteElement={deleteElement}
                      />
                    ))}
                    {elements.length === 0 && (
                      <div className="flex h-full items-center justify-center text-sm text-gray-400">
                        Drag and drop elements to build your email template
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="col-span-4">
              {/* Removed tabs structure, keeping only the elements panel and properties panel */}
              <div className="space-y-4">
                {selectedElement ? (
                  <ElementConfigPanel
                    selectedElement={selectedElement}
                    updateElement={updateElement}
                    contentEditRef={contentEditRef}
                  />
                ) : (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="mb-4 font-medium">Email Elements</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.TEXT)}
                        >
                          <Type className="mr-2 h-4 w-4" />
                          <span>Text</span>
                        </UIButton>
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.HEADING)}
                        >
                          <Type className="mr-2 h-5 w-5" />
                          <span>Heading</span>
                        </UIButton>
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.BUTTON)}
                        >
                          <div className="mr-2 flex h-4 w-4 items-center justify-center rounded-sm bg-primary">
                            <ChevronRight className="h-3 w-3 text-white" />
                          </div>
                          <span>Button</span>
                        </UIButton>
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.IMAGE)}
                        >
                          <ImageIcon className="mr-2 h-4 w-4" />
                          <span>Image</span>
                        </UIButton>
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.DIVIDER)}
                        >
                          <Minus className="mr-2 h-4 w-4" />
                          <span>Divider</span>
                        </UIButton>
                        <UIButton
                          variant="outline"
                          className="flex h-auto items-center justify-start px-3 py-3"
                          onClick={() => addElement(ItemType.SPACER)}
                        >
                          <ChevronUp className="mr-2 h-4 w-4" />
                          <ChevronDown className="-ml-2 mr-2 h-4 w-4" />
                          <span>Spacer</span>
                        </UIButton>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

// Helper component for button element
const Minus = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChevronRight = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default EmailTemplateBuilder;
